import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  RefreshCw, Loader2, ClipboardList, FileQuestion, AlertTriangle,
  Bell, Scale, FileDiff, FileImage, Package, Truck, FileText,
  CheckSquare, Plus, AlertCircle, ChevronRight, BarChart3, Milestone, HardHat,
  FileBarChart, BookOpen, Wrench
} from 'lucide-react';
import ResponseTrackerPanel from '@/components/ResponseTrackerPanel';

export default function ProjectOverviewTab({
  projectId,
  projectSummary,
  summaryLoading,
  loadProjectSummary,
  setActiveTab,
  events,
  canCreateEvents,
  canCreateRFI,
  canCreateSI,
  setRfiFormOpen,
  awaitingItemsCount = 0,
  awaitingOverdueCount = 0
}) {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button
        onClick={() => navigate('/')}
        variant="ghost"
        className="text-slate-600 hover:text-slate-900"
        data-testid="overview-back-button"
      >
        ← Back to Dashboard
      </Button>

      {/* Items Requiring Attention Banner */}
      {awaitingItemsCount > 0 && (
        <div 
          onClick={() => setActiveTab('awaiting')}
          className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg cursor-pointer hover:shadow-md transition-all group"
          data-testid="awaiting-items-banner"
        >
          <div className="flex items-center gap-4">
            <div className="p-2 bg-amber-100 rounded-full">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">
                {awaitingItemsCount} Item{awaitingItemsCount !== 1 ? 's' : ''} Requiring Attention
              </h3>
              <p className="text-sm text-slate-600">
                {awaitingOverdueCount > 0 ? (
                  <span className="text-red-600 font-medium">{awaitingOverdueCount} overdue</span>
                ) : (
                  'Review and action outstanding items'
                )}
                {awaitingOverdueCount > 0 && awaitingItemsCount > awaitingOverdueCount && (
                  <span className="text-slate-500"> · {awaitingItemsCount - awaitingOverdueCount} pending</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-amber-700">
            <span className="text-sm font-medium hidden sm:inline">View All</span>
            <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      )}
      
      {/* Response Tracker */}
      <ResponseTrackerPanel projectId={projectId} />

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-barlow font-semibold uppercase">Project Overview</h2>
        <Button 
          variant="outline" 
          onClick={loadProjectSummary} 
          disabled={summaryLoading}
          className="rounded-sm"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${summaryLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {summaryLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* Items Requiring Attention */}
          <Card 
            className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('awaiting')}
            data-testid="overview-card-awaiting"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <h3 className="font-bold text-slate-800">Requires Attention</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-amber-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{awaitingItemsCount || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-amber-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{Math.max(0, (awaitingItemsCount || 0) - (awaitingOverdueCount || 0))}</p>
                  <p className="text-xs text-slate-500 uppercase">Pending</p>
                </div>
                <div className="cursor-pointer hover:bg-amber-100 rounded p-2">
                  <p className="text-2xl font-bold text-red-600">{awaitingOverdueCount || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Site Instructions */}
          <Card 
            className="border-blue-200 bg-blue-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('si')}
            data-testid="overview-card-si"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="h-5 w-5 text-blue-600" />
                <h3 className="font-bold text-slate-800">Site Instructions</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-blue-100 rounded p-2" onClick={(e) => { e.stopPropagation(); setActiveTab('si'); }}>
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.si?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-blue-100 rounded p-2" onClick={(e) => { e.stopPropagation(); setActiveTab('si'); }}>
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.si?.open || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Open</p>
                </div>
                <div className="cursor-pointer hover:bg-blue-100 rounded p-2" onClick={(e) => { e.stopPropagation(); setActiveTab('si'); }}>
                  <p className="text-2xl font-bold text-red-600">{projectSummary?.si?.overdue || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* RFIs */}
          <Card 
            className="border-purple-200 bg-purple-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('rfi')}
            data-testid="overview-card-rfi"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileQuestion className="h-5 w-5 text-purple-600" />
                <h3 className="font-bold text-slate-800">RFIs</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-purple-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.rfis?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-purple-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.rfis?.open || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Open</p>
                </div>
                <div className="cursor-pointer hover:bg-purple-100 rounded p-2">
                  <p className="text-2xl font-bold text-red-600">{projectSummary?.rfis?.overdue || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delay Events */}
          <Card 
            className="border-amber-200 bg-amber-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('events')}
            data-testid="overview-card-events"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <h3 className="font-bold text-slate-800">Delay Events</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-amber-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.events?.total || events.length}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-amber-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.events?.open || events.filter(e => e.status !== 'Closed' && e.status !== 'Resolved').length}</p>
                  <p className="text-xs text-slate-500 uppercase">Open</p>
                </div>
                <div className="cursor-pointer hover:bg-amber-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-600">{projectSummary?.events?.draft || events.filter(e => e.status === 'Draft').length}</p>
                  <p className="text-xs text-slate-500 uppercase">Draft</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notices */}
          <Card 
            className="border-orange-200 bg-orange-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('notices')}
            data-testid="overview-card-notices"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="h-5 w-5 text-orange-600" />
                <h3 className="font-bold text-slate-800">Notices</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-orange-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.notices?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-orange-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.notices?.draft || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Draft</p>
                </div>
                <div className="cursor-pointer hover:bg-orange-100 rounded p-2">
                  <p className="text-2xl font-bold text-green-600">{projectSummary?.notices?.submitted || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Submitted</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Claims */}
          <Card 
            className="border-red-200 bg-red-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('claims')}
            data-testid="overview-card-claims"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Scale className="h-5 w-5 text-red-600" />
                <h3 className="font-bold text-slate-800">Claims</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-red-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.claims?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-red-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.claims?.submitted || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Submitted</p>
                </div>
                <div className="cursor-pointer hover:bg-red-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-600">{projectSummary?.claims?.pending || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Variation Orders */}
          <Card 
            className="border-purple-200 bg-purple-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => navigate(`/projects/${projectId}/vos`)}
            data-testid="overview-card-vos"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileDiff className="h-5 w-5 text-purple-600" />
                <h3 className="font-bold text-slate-800">Variation Orders</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-purple-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.vos?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-purple-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.vos?.pending || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Pending</p>
                </div>
                <div className="cursor-pointer hover:bg-purple-100 rounded p-2">
                  <p className="text-2xl font-bold text-green-600">{projectSummary?.vos?.approved || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Drawings */}
          <Card 
            className="border-teal-200 bg-teal-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('drawings')}
            data-testid="overview-card-drawings"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileImage className="h-5 w-5 text-teal-600" />
                <h3 className="font-bold text-slate-800">Drawings</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-teal-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.drawings?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-teal-100 rounded p-2">
                  <p className="text-2xl font-bold text-blue-600">{projectSummary?.drawings?.recent || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Recent</p>
                </div>
                <div className="cursor-pointer hover:bg-teal-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.drawings?.pending || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shop Drawings */}
          <Card 
            className="border-indigo-200 bg-indigo-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => navigate(`/projects/${projectId}/shop-drawings`)}
            data-testid="overview-card-shop-drawings"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Package className="h-5 w-5 text-indigo-600" />
                <h3 className="font-bold text-slate-800">Shop Drawings</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-indigo-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.shopDrawings?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-indigo-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">
                    {projectSummary?.shopDrawings?.under_review || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase">Pending</p>
                </div>
                <div className="cursor-pointer hover:bg-indigo-100 rounded p-2">
                  <p className="text-2xl font-bold text-green-600">
                    {projectSummary?.shopDrawings?.approved || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase">Approved</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Procurement Schedule */}
          <Card 
            className="border-orange-200 bg-orange-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => navigate(`/projects/${projectId}/procurement`)}
            data-testid="overview-card-procurement"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Truck className="h-5 w-5 text-orange-600" />
                <h3 className="font-bold text-slate-800">Procurement</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-orange-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.procurement?.total_items || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-orange-100 rounded p-2">
                  <p className="text-2xl font-bold text-blue-600">{projectSummary?.procurement?.in_progress_count || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">In Progress</p>
                </div>
                <div className="cursor-pointer hover:bg-orange-100 rounded p-2">
                  <p className="text-2xl font-bold text-red-600">{projectSummary?.procurement?.delayed_count || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Delayed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Information Required Schedule */}
          <Card 
            className="border-violet-200 bg-violet-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => navigate(`/projects/${projectId}/irs`)}
            data-testid="overview-card-irs"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="h-5 w-5 text-violet-600" />
                <h3 className="font-bold text-slate-800">Info Required</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-violet-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.irs?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-violet-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.irs?.outstanding || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Outstanding</p>
                </div>
                <div className="cursor-pointer hover:bg-violet-100 rounded p-2">
                  <p className="text-2xl font-bold text-red-600">{projectSummary?.irs?.late || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Late</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Meeting Minutes */}
          <Card 
            className="border-cyan-200 bg-cyan-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => navigate(`/projects/${projectId}/meeting-minutes`)}
            data-testid="overview-card-meeting-minutes"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="h-5 w-5 text-cyan-600" />
                <h3 className="font-bold text-slate-800">Meeting Minutes</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-cyan-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.meetingMinutes?.total_meetings || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Meetings</p>
                </div>
                <div className="cursor-pointer hover:bg-cyan-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.meetingMinutes?.open_actions || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Open</p>
                </div>
                <div className="cursor-pointer hover:bg-cyan-100 rounded p-2">
                  <p className="text-2xl font-bold text-red-600">{projectSummary?.meetingMinutes?.overdue_actions || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tasks */}
          <Card 
            className="border-green-200 bg-green-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => navigate('/tasks')}
            data-testid="overview-card-tasks"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <CheckSquare className="h-5 w-5 text-green-600" />
                <h3 className="font-bold text-slate-800">Tasks</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-green-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.tasks?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-green-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.tasks?.open || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Open</p>
                </div>
                <div className="cursor-pointer hover:bg-green-100 rounded p-2">
                  <p className="text-2xl font-bold text-red-600">{projectSummary?.tasks?.overdue || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Programs */}
          <Card 
            className="border-violet-200 bg-violet-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('programs')}
            data-testid="overview-card-programs"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="h-5 w-5 text-violet-600" />
                <h3 className="font-bold text-slate-800">Programs</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-violet-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.programs?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-violet-100 rounded p-2">
                  <p className="text-2xl font-bold text-green-600">{projectSummary?.programs?.analyzed || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Analyzed</p>
                </div>
                <div className="cursor-pointer hover:bg-violet-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.programs?.pending || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Key Milestones */}
          <Card 
            className="border-rose-200 bg-rose-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('overview')}
            data-testid="overview-card-milestones"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Milestone className="h-5 w-5 text-rose-600" />
                <h3 className="font-bold text-slate-800">Key Milestones</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-rose-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.milestones?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-rose-100 rounded p-2">
                  <p className="text-2xl font-bold text-blue-600">{projectSummary?.milestones?.upcoming || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Upcoming</p>
                </div>
                <div className="cursor-pointer hover:bg-rose-100 rounded p-2">
                  <p className="text-2xl font-bold text-green-600">{projectSummary?.milestones?.past || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Achieved</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sub-Contractors */}
          <Card 
            className="border-cyan-200 bg-cyan-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => setActiveTab('subcontractors')}
            data-testid="overview-card-subcontractors"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <HardHat className="h-5 w-5 text-cyan-600" />
                <h3 className="font-bold text-slate-800">Sub-Contractors</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-cyan-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.subcontractors?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-cyan-100 rounded p-2">
                  <p className="text-2xl font-bold text-green-600">{projectSummary?.subcontractors?.active || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Active</p>
                </div>
                <div className="cursor-pointer hover:bg-cyan-100 rounded p-2">
                  <p className="text-2xl font-bold text-blue-600">{projectSummary?.subcontractors?.onSite || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">On Site</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Periodic Reports */}
          <Card 
            className="border-sky-200 bg-sky-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => navigate(`/projects/${projectId}/periodic-reports`)}
            data-testid="overview-card-periodic-reports"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <FileBarChart className="h-5 w-5 text-sky-600" />
                <h3 className="font-bold text-slate-800">Periodic Reports</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-sky-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.periodicReports?.total_cycles || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Cycles</p>
                </div>
                <div className="cursor-pointer hover:bg-sky-100 rounded p-2">
                  <p className="text-2xl font-bold text-green-600">{projectSummary?.periodicReports?.reports_submitted || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Submitted</p>
                </div>
                <div className="cursor-pointer hover:bg-sky-100 rounded p-2">
                  <p className="text-2xl font-bold text-red-600">{projectSummary?.periodicReports?.reports_overdue || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Daily Site Diary */}
          <Card 
            className="border-emerald-200 bg-emerald-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => navigate(`/projects/${projectId}/site-diary`)}
            data-testid="overview-card-site-diary"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="h-5 w-5 text-emerald-600" />
                <h3 className="font-bold text-slate-800">Site Diary</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-emerald-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.siteDiary?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-emerald-100 rounded p-2">
                  <p className="text-2xl font-bold text-green-600">{projectSummary?.siteDiary?.completed || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Complete</p>
                </div>
                <div className="cursor-pointer hover:bg-emerald-100 rounded p-2">
                  <p className="text-2xl font-bold text-amber-600">{projectSummary?.siteDiary?.draft || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Draft</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Plant & Equipment */}
          <Card 
            className="border-stone-200 bg-stone-50/30 hover:shadow-md transition-shadow cursor-pointer rounded-sm"
            onClick={() => navigate(`/projects/${projectId}/plant`)}
            data-testid="overview-card-plant"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="h-5 w-5 text-stone-600" />
                <h3 className="font-bold text-slate-800">Plant & Equipment</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="cursor-pointer hover:bg-stone-100 rounded p-2">
                  <p className="text-2xl font-bold text-slate-900">{projectSummary?.plant?.total || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                </div>
                <div className="cursor-pointer hover:bg-stone-100 rounded p-2">
                  <p className="text-2xl font-bold text-green-600">{projectSummary?.plant?.active || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Active</p>
                </div>
                <div className="cursor-pointer hover:bg-stone-100 rounded p-2">
                  <p className="text-2xl font-bold text-red-600">{projectSummary?.plant?.breakdown || 0}</p>
                  <p className="text-xs text-slate-500 uppercase">Breakdown</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}
